<script setup>
import { computed, ref } from 'vue'
import 养老系统PdfUrl from '@/assets/docs/数智健康养老系统PPT新.pdf'
import 慢病系统PdfUrl from '@/assets/docs/天唯士慢病管理平台v1.2.pdf'

// 示例文档数据
const documents = ref([
  {
    id: 1,
    title: '数智健康养老系统',
    type: 'pdf',
    description: '',
    
    url: 养老系统PdfUrl
  },
  {
    id: 2,
    title: '慢病管理平台',
    type: 'pdf',
    description: '',
    url: 慢病系统PdfUrl,
  }
//   {
//     id: 2,
//     title: '医院信息化整体解决方案',
//     type: 'ppt',
//     description: '医院信息化建设总体规划 PPT，包含 HIS/LIS/PACS/EMR 等子系统规划。',
//     // TODO: 将此处替换为公网可访问的 PPT/PPTX 地址
//     url: 'https://example.com/files/hospital-it-solution.pptx'
//   }
])

const activeId = ref(documents.value[0]?.id || null)

const activeDoc = computed(() =>
  documents.value.find(item => item.id === activeId.value) || null
)

// 根据类型生成实际用于 iframe 的预览地址
const viewerUrl = computed(() => {
  if (!activeDoc.value) return ''
  const { type, url } = activeDoc.value

  if (type === 'ppt' || type === 'pptx') {
    // 说明：这里使用微软 Office 在线预览服务，要求 url 能在公网直接访问
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
  }

  // PDF 直接使用浏览器内置预览能力
  return `${url}#toolbar=1&view=FitH`
})
</script>

<template>
  <div class="doc-page">
    <div class="doc-layout">
      <!-- 左侧文档列表 -->
      <aside class="doc-sidebar">
        <div class="sidebar-header">
          <h2>资料中心</h2>
          <p>产品宣传册、解决方案 PPT 等文档</p>
        </div>
        <el-menu
          class="doc-menu"
          :default-active="String(activeId)"
          @select="(key) => (activeId = Number(key))"
        >
          <el-menu-item
            v-for="item in documents"
            :key="item.id"
            :index="String(item.id)"
          >
            <div class="menu-item-content">
              <span class="title">{{ item.title }}</span>
              <el-tag
                size="small"
                :type="item.type === 'pdf' ? 'success' : 'primary'"
              >
                {{ item.type.toUpperCase() }}
              </el-tag>
            </div>
            <!-- <div class="menu-item-desc">
              {{ item.description }}
            </div> -->
          </el-menu-item>
        </el-menu>
      </aside>

      <!-- 右侧预览区域 -->
      <section class="doc-content">
        <el-card v-if="activeDoc" class="doc-card" shadow="always">
          <template #header>
            <div class="doc-header">
              <div>
                <h2 class="doc-title">{{ activeDoc.title }}</h2>
                <p class="doc-subtitle">
                  {{ activeDoc.description }}
                </p>
              </div>
              <div class="doc-header-actions">
                <el-tag
                  effect="dark"
                  :type="activeDoc.type === 'pdf' ? 'success' : 'primary'"
                >
                  {{ activeDoc.type.toUpperCase() }}
              </el-tag>
              </div>
            </div>
          </template>

          <div class="viewer-wrapper">
            <div v-if="viewerUrl" class="viewer-inner">
              <iframe
                :src="viewerUrl"
                frameborder="0"
                allowfullscreen
              ></iframe>
            </div>
            <el-empty
              v-else
              description="暂时无法预览该文档，请检查地址是否正确"
            />
          </div>

          <div class="doc-tip">
            <p v-if="activeDoc.type === 'ppt' || activeDoc.type === 'pptx'">
              * PPT 预览依赖微软 Office 在线服务，需保证文档地址可在公网访问；
              如在内网环境，可将 PPT 先转换为 PDF 后再上传预览。
            </p>
            <!-- <p v-else>
              * 若内嵌预览异常，可点击右上角
              <strong>「新窗口打开」</strong> 直接下载或在浏览器中查看。
            </p> -->
          </div>
        </el-card>

        <el-empty
          v-else
          description="暂无可预览文档"
          class="doc-empty"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
.doc-page {
  padding: 24px 0;
}

.doc-layout {
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.doc-sidebar {
  width: 280px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 20px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
}

.sidebar-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.sidebar-header p {
  margin: 8px 0 0;
  font-size: 13px;
  color: #999;
}

.doc-menu {
  border-right: none;
  flex: 1;
}

::v-deep(.el-menu-item) {
  height: auto;
  line-height: 1.5;
  padding-top: 12px;
  padding-bottom: 12px;
  white-space: normal;
}

::v-deep(.el-menu-item.is-active) {
  background-color: #fff;
}

.menu-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  background-color: #fff;
}

.menu-item-content .title {
  font-size: 14px;
  color: #333;
  background-color: #fff;
  font-weight: 500;
  margin-right: 8px;
}

.menu-item-desc {
  font-size: 12px;
  color: #999;
}

.doc-content {
  flex: 1;
}

.doc-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.doc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.doc-title {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.doc-subtitle {
  margin: 8px 0 0;
  font-size: 13px;
  color: #666;
}

.doc-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.viewer-wrapper {
  margin-top: 8px;
}

.viewer-inner {
  position: relative;
  width: 100%;
  /* 16:9 比例预览区域 */
  padding-bottom: 56.25%;
  background-color: #000;
  border-radius: 6px;
  overflow: hidden;
}

.viewer-inner iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.doc-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #999;
}

.doc-tip strong {
  color: #333;
}

.doc-empty {
  background-color: #fff;
  border-radius: 8px;
}

@media (max-width: 960px) {
  .doc-layout {
    flex-direction: column;
  }

  .doc-sidebar {
    width: 100%;
  }
}
</style>

